// This file contains the basic implementation of a
// component with the on-off behavior.

import React, { forwardRef, type ComponentProps } from "react";
import { CSS, styled } from "~/application/theme";

export type InputStandardProps = {
  id?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  role?: string;
  tabIndex?: number;
  disabled?: boolean;
  defaultValue?: any;
  name?: string;
  title?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  maxLength?: number;
  minLength?: number;
  step?: string | number;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  type?: string;
  value?: any;
  size?: "sm" | "md" | "smlg";
  variant?: "underlined";
  css?: CSS;
};

export type InputBaseProps = ComponentProps<typeof InputBaseRoot> &
  InputStandardProps & {
    inputProps?: ComponentProps<typeof InputBaseInput>;
    leftAdornment?: React.ReactElement;
    rightAdornment?: React.ReactElement;
    warning?: boolean;
    dataCy?: string;
  };

export const InputBaseInput = styled("input", {
  // Reset
  all: "unset",
  boxSizing: "content-box",
  display: "block",

  // Custom
  font: "inherit",
  letterSpacing: "inherit",
  color: "currentColor",
  border: 0,
  margin: "0",
  marginLeft: "2px",
  minWidth: "0",
  width: "100%",

  "&::placeholder": {
    color: "$neutral-500",
  },

  "&:read-only": {
    cursor: "default",
  },
});

const InputBaseRoot = styled("div", {
  // Reset
  boxSizing: "border-box",
  position: "relative",
  display: "inline-flex",

  // Reset custom
  cursor: "text",

  // Custom
  alignItems: "center",
  borderRadius: "$md",
  transition: "$normal",
  color: "$neutral-900",
  fontSize: "$md",
  lineHeight: "$fontSizes$md",
  backgroundColor: "$neutrals-white",

  variants: {
    size: {
      sm: {
        padding: "0 $4",
        [`& > ${InputBaseInput}`]: {
          height: "$11",
        },

        "@mxlg": {
          [`& > ${InputBaseInput}`]: {
            height: "$10",
          },
        },
      },
      md: {
        padding: "0 $4",
        "@mxmd": {
          padding: "0 $6",
        },
        [`& > ${InputBaseInput}`]: {
          height: "$12",
        },

        "@mxlg": {
          [`& > ${InputBaseInput}`]: {
            height: "$10",
          },
        },
      },
      smlg: {
        padding: "0 $4",
        width: "390px",
        [`& > ${InputBaseInput}`]: {
          height: "$16",
        },
      },
    },
    variant: {
      underlined: {
        padding: 0,
        textDecoration: "underline",
        textUnderlineOffset: "10px",
        backgroundColor: "transparent",
        fontSize: "$4xl",
        fontWeight: "700",
        lineHeight: 1.4,
        color: "$primary-700",
        textAlign: "center",

        [`& > ${InputBaseInput}`]: {
          height: "auto",
        },

        "&::after": {
          border: "none",
        },

        "&[data-focused=true]": {
          boxShadow: "none",
        },

        "&[data-dirty=true]": {
          backgroundColor: "transparent",
        },

        "&[data-disabled=true]": {
          pointerEvents: "none",
          cursor: "default",
          backgroundColor: "transparent",
          color: "$primary-700",
        },
      },
    },
  },

  "&::after": {
    content: "",
    pointerEvents: "none",
    position: "absolute",
    inset: 0,
    margin: 0,

    borderColor: "$neutral-300",
    borderRadius: "inherit",
    borderStyle: "solid",
    borderWidth: "1px",

    transition: "$faster",
  },

  "&:hover::after": {
    borderWidth: "2px",
    borderColor: "$primary-600",
  },

  "&[data-focused=true]": {
    boxShadow: "$focus-md",
  },

  "&[data-filled=true]::after": {
    borderWidth: "2px",
    borderColor: "$primary-600",
  },

  "&[data-dirty=true]": {
    backgroundColor: "$error-light",
    "&::after": {
      borderWidth: "2px",
      borderColor: "$danger-500",
    },
  },

  "&[data-disabled=true]": {
    pointerEvents: "none",
    cursor: "default",
    backgroundColor: "$neutral-100",
    color: "$neutral-300",

    "::placeholder": {
      color: "$neutral-300",
    },

    "&::after": {
      borderWidth: "1px",
      borderColor: "$neutral-300",
    },

    svg: {
      fill: "$neutral-300",
    },
  },

  "&[data-warning=true]": {
    backgroundColor: "$warning-light",
    "&::after": {
      borderWidth: "2px",
      borderColor: "$warning-base",
    },
  },

  "&[data-readonly=true]": {
    cursor: "default",
  },

  defaultVariants: {
    size: "md",
  },
});

export const InputBase = forwardRef<React.ElementRef<typeof InputBaseRoot>, InputBaseProps>(
  (
    {
      id,
      autoComplete,
      autoFocus,
      disabled,
      defaultValue,
      name,
      role,
      tabIndex,
      onBlur,
      onChange,
      onClick,
      onFocus,
      onKeyDown,
      onKeyUp,
      onPaste,
      maxLength,
      minLength,
      step,
      placeholder,
      readOnly,
      required,
      type,
      value,
      warning = false,
      dataCy,
      // Root props
      inputProps,
      leftAdornment,
      rightAdornment,
      ...props
    },
    forwardedRef,
  ) => {
    const inputRef = React.createRef<HTMLInputElement>();
    const [focused, setFocused] = React.useState(false);
    const [filledState, setFilled] = React.useState(false);
    const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
      if (onFocus) onFocus(event);

      setFocused(true);
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
      if (onBlur) onBlur(event);

      setFocused(false);
    };

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      if (onChange) onChange(event);

      setFilled(event.target.value.length > 0);
    };

    const handleClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
      if (inputRef.current && event.currentTarget === event.target) {
        inputRef.current.focus();
      }

      if (onClick) onClick(event);
    };

    const filled = Boolean(value?.length) ?? filledState;

    return (
      <InputBaseRoot
        data-focused={focused}
        data-filled={filled}
        data-disabled={disabled}
        data-readonly={readOnly}
        onClick={handleClick}
        ref={forwardedRef}
        data-cy={dataCy}
        data-warning={warning}
        {...props}
      >
        {leftAdornment}
        <InputBaseInput
          id={id}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          disabled={disabled}
          defaultValue={defaultValue}
          minLength={minLength}
          maxLength={maxLength}
          step={step}
          name={name}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onPaste={onPaste}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          type={type}
          value={value}
          role={role}
          tabIndex={tabIndex}
          {...inputProps}
          ref={inputRef}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
        />
        {rightAdornment}
      </InputBaseRoot>
    );
  },
);

InputBase.displayName = "InputBase";
